import type { CitySlug, ServiceSlug } from '@/types'

import { isCitySlug } from '@/constants/cities'
import { isServiceSlug } from '@/constants/services'

export type EnquiryValues = {
    name: string
    phone: string
    email: string
    company: string
    city: string
    quantity: string
    message: string
    services: Array<ServiceSlug>
}

export type EnquiryField = keyof EnquiryValues

export type EnquiryErrors = Partial<Record<EnquiryField, string>>

export type EnquiryDefaults = {
    service?: ServiceSlug
    city?: CitySlug
}

export const OTHER_CITY = 'other'

export const QUANTITY_OPTIONS: Array<string> = [
    "1 to 10",
    "11 to 50",
    "51 to 200",
    "More than 200"
]

export const ENQUIRY_LIMITS = {
    name: 100,
    company: 120,
    email: 254,
    message: 2000
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export const phoneDigits = (value: string): string => value.replace(/\D/g, '')

export const emptyEnquiry = (defaults: EnquiryDefaults = {}): EnquiryValues => ({
    name: '',
    phone: '',
    email: '',
    company: '',
    city: defaults.city ?? '',
    quantity: '',
    message: '',
    services: defaults.service ? [defaults.service] : []
})

export const validateField = (field: EnquiryField, values: EnquiryValues): string | undefined => {
    switch (field) {
        case 'name': {
            const name = values.name.trim()
            if (name.length < 2) return "Enter your name so we know who to call."
            if (name.length > ENQUIRY_LIMITS.name) return "Use 100 characters or fewer."
            return undefined
        }
        case 'phone': {
            const digits = phoneDigits(values.phone)
            if (!digits) return "Enter your phone number so we can call you back."
            if (digits.length < 10 || digits.length > 13) return "Enter a valid phone number, for example 98765 43210."
            return undefined
        }
        case 'email': {
            const email = values.email.trim()
            if (!email) return undefined
            if (email.length > ENQUIRY_LIMITS.email || !EMAIL_PATTERN.test(email)) return "Enter a valid email address, like name@company.com."
            return undefined
        }
        case 'company':
            return values.company.trim().length > ENQUIRY_LIMITS.company ? "Use 120 characters or fewer." : undefined
        case 'services':
            return values.services.length === 0 ? "Choose at least one service." : undefined
        case 'message':
            return values.message.trim().length > ENQUIRY_LIMITS.message ? "Keep your requirement under 2,000 characters." : undefined
        default:
            return undefined
    }
}

export const ENQUIRY_FIELDS: Array<EnquiryField> = ['services', 'name', 'phone', 'email', 'company', 'city', 'quantity', 'message']

export const validateEnquiry = (values: EnquiryValues): EnquiryErrors => {
    const errors: EnquiryErrors = {}

    for (const field of ENQUIRY_FIELDS) {
        const error = validateField(field, values)
        if (error) errors[field] = error
    }

    return errors
}

const readText = (value: unknown): string => {
    const raw = Array.isArray(value) ? value[0] : value
    return typeof raw === 'string' ? raw.trim().slice(0, 5000) : ''
}

const readList = (value: unknown): Array<string> => {
    if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string')
    return typeof value === 'string' && value ? [value] : []
}

export const normalizeEnquiry = (input: Record<string, unknown>): EnquiryValues => {
    const city = readText(input.city)
    const quantity = readText(input.quantity)
    const services = Array.from(new Set(readList(input.services).filter(isServiceSlug)))

    return {
        name: readText(input.name),
        phone: readText(input.phone),
        email: readText(input.email),
        company: readText(input.company),
        city: isCitySlug(city) || city === OTHER_CITY ? city : '',
        quantity: QUANTITY_OPTIONS.includes(quantity) ? quantity : '',
        message: readText(input.message),
        services
    }
}
